import { resolvePostHogDeploymentProperties } from '../../../lib/deployment-environment';
import { isPostHogCaptureAllowed } from '../../../lib/observability/posthog-capture-gate';
import {
  internalTestAnalyticsProperties,
  internalTestWorkspaceSlugFromPath,
} from '../../../lib/observability/internal-test-workspace';
import {
  USER_BLOCKING_FAILURE_EVENT,
  type UserBlockingFailureProperties,
} from '../../../lib/observability/user-blocking-failure';

/** posthog-js's own singleton, already loaded wherever a caller imports it. */
export type LoadedPostHogClient = {
  __loaded?: boolean;
  capture(
    event: string,
    properties: Record<string, unknown>,
    options?: { send_instantly?: boolean }
  ): unknown;
};

/** Same-origin ingest proxy the browser client uses (`api_host`). */
const INGEST_PATH = '/ph-ingest/i/v0/e/';

/**
 * Sends one `user_blocking_failure` without loading anything (#6155 gap): the
 * caller's already-loaded posthog-js when it is initialized, else a beacon to
 * the ingest proxy. Both carry the properties the #alerting destination
 * filters on. For failures that can stop every lazy chunk from loading.
 */
export function sendUserBlockingFailureFromLoadedCode(
  properties: UserBlockingFailureProperties,
  client: LoadedPostHogClient | null | undefined
): 'posthog' | 'beacon' | 'none' {
  if (client?.__loaded) {
    client.capture(
      USER_BLOCKING_FAILURE_EVENT,
      { ...properties },
      {
        send_instantly: true,
      }
    );
    return 'posthog';
  }
  return beaconUserBlockingFailure(properties) ? 'beacon' : 'none';
}

/**
 * The same decision as the browser client's init gate (a key, and not
 * localhost unless local analytics are on) plus posthog-js's persisted
 * opt-out (`__ph_opt_in_out_<key>` = '0'), so the fallback never sends what
 * the app or the user chose not to capture.
 */
function beaconAllowed(apiKey: string): boolean {
  if (
    !isPostHogCaptureAllowed({
      apiKey,
      hostname: window.location?.hostname,
      enableLocalAnalytics:
        process.env.NEXT_PUBLIC_ENABLE_LOCAL_ANALYTICS === 'true',
    })
  )
    return false;
  const stored = readOptInOut(`__ph_opt_in_out_${apiKey}`);
  return stored !== '0' && stored !== 'false';
}

/**
 * posthog-js's persisted opt-in/out value, from localStorage then the cookie.
 * Every read fails open: only a value that reads back as opted out stops the
 * alert, never blocked storage or a malformed cookie.
 */
function readOptInOut(optKey: string): string | null {
  try {
    const stored = window.localStorage?.getItem(optKey);
    if (stored !== null && stored !== undefined) return stored;
  } catch {
    // Blocked storage: try the cookie.
  }
  try {
    const cookie = document.cookie
      ?.split('; ')
      .find((entry) => entry.startsWith(`${optKey}=`));
    return cookie ? decodeURIComponent(cookie.slice(optKey.length + 1)) : null;
  } catch {
    // A blocked cookie jar or a malformed escape reads as "no opt-out".
    return null;
  }
}

function beaconUserBlockingFailure(
  properties: UserBlockingFailureProperties
): boolean {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (
    !apiKey ||
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function' ||
    !beaconAllowed(apiKey)
  )
    return false;
  const pathname = window.location?.pathname;
  const internalSlug = internalTestWorkspaceSlugFromPath(pathname);
  // Personless: a fresh id, sent where the capture API and the SDK put it.
  const distinctId = `user-blocking-beacon-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
  const body = JSON.stringify({
    api_key: apiKey,
    event: USER_BLOCKING_FAILURE_EVENT,
    distinct_id: distinctId,
    properties: {
      ...resolvePostHogDeploymentProperties({
        convexUrl: process.env.NEXT_PUBLIC_CONVEX_URL,
        hostname: window.location?.hostname,
        imaiEnvironment: process.env.NEXT_PUBLIC_IMAI_ENV,
        vercelGitCommitRef: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF,
        vercelGitCommitSha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        vercelTargetEnvironment:
          process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV ||
          process.env.NEXT_PUBLIC_VERCEL_ENV,
      }),
      ...(internalSlug ? internalTestAnalyticsProperties(internalSlug) : {}),
      ...properties,
      distinct_id: distinctId,
      delivery: 'beacon',
      $process_person_profile: false,
    },
  });
  return navigator.sendBeacon(
    INGEST_PATH,
    new Blob([body], { type: 'application/json' })
  );
}
